import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline, MaskedLine } from "@/components/Reveal";
import { API, trackEvent } from "@/lib/api";

const imgSrc = (img) => (img && !img.startsWith("http") && !img.startsWith("/") ? `${API}/files/${img}` : img);

export default function Resources() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Resources — Wired 2 Win Golf";
    trackEvent("page_view", { page: "resources" });
    axios.get(`${API}/posts`).then(({ data }) => setPosts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="resources-page" className="pt-32 lg:pt-40">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <Overline className="mb-6"><MaskedLine>Resources</MaskedLine></Overline>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl uppercase">
          <MaskedLine delay={0.15}>Train the</MaskedLine>
          <MaskedLine delay={0.3}><span className="text-volt">90 percent.</span></MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-8 max-w-xl text-white/60 text-base sm:text-lg leading-relaxed">
            Drills, frameworks and field notes for the mental side of the game. New drops weekly — subscribers get them first.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        {loading ? (
          <p data-testid="resources-loading" className="text-white/40 text-sm">Loading the library...</p>
        ) : posts.length === 0 ? (
          <p data-testid="resources-empty" className="text-white/40 text-sm">Fresh content is on the tee. Check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resources-grid">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className={i === 0 ? "md:col-span-2" : ""}>
                <Link
                  to={`/resources/${p.slug}`}
                  data-testid={`post-card-${p.slug}`}
                  className="group block border border-white/10 bg-ink hover:border-volt/60 transition-colors duration-300 h-full"
                >
                  {p.image ? (
                    <div className="overflow-hidden">
                      <img
                        src={imgSrc(p.image)}
                        alt={p.title}
                        loading="lazy"
                        className={`w-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700 ${i === 0 ? "aspect-[21/9]" : "aspect-[16/10]"}`}
                      />
                    </div>
                  ) : (
                    <div className={`w-full bg-ink border-b border-white/10 flex items-center px-7 ${i === 0 ? "aspect-[21/9]" : "aspect-[16/10]"}`}>
                      <span className="font-display font-black uppercase tracking-tight text-white/10 text-3xl">Wired2Win</span>
                    </div>
                  )}
                  <div className="p-7">
                    <p className="text-[11px] tracking-[0.25em] uppercase font-bold text-volt mb-3">
                      {new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                    <h2 className="font-display text-2xl font-bold tracking-tight leading-tight mb-3 group-hover:text-volt transition-colors">
                      {p.title}
                    </h2>
                    <p className="text-white/50 text-sm leading-relaxed line-clamp-2">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 mt-5 text-xs font-bold uppercase tracking-widest text-white/60 group-hover:text-white transition-colors">
                      Read <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
